/**
 * Building List Component
 * 
 * Displays all buildings with their counts and images.
 */

import { getTileImageKey } from '../utils/TileImageMap.js';
import {
    CUTTER, SAWMILL, QUARRY, STONEMASON, MINE, FOUNDRY,
    FARM, MILL, MANOR, BAZAAR, WHARF,
    CASA, CELLARIUM, CACHE,
    BARRACKS, CASTLE, CITY_GUARD_HOUSE, SHIPYARD,
    MOONGLOW, STABLES, TEMPLE, TRAINING_GROUND, WORKSHOP
} from '../core/Constants.js';

export class BuildingList {
    constructor(containerElement, gameEngine, assetLoader) {
        this.container = containerElement;
        this.gameEngine = gameEngine;
        this.assetLoader = assetLoader;
        this.layoutMetadata = null; // Store Remarks and Notes from loaded layout
        
        // Define all buildings with their display names
        this.buildings = [
            { type: CUTTER, name: 'Forester\'s Hut', key: 'building_forester' },
            { type: SAWMILL, name: 'Sawmill', key: 'building_sawmill' },
            { type: QUARRY, name: 'Stone Mine', key: 'building_quarry' },
            { type: STONEMASON, name: 'Mason\'s Hut', key: 'building_stonemason' },
            { type: MINE, name: 'Iron Mine', key: 'building_mine' },
            { type: FOUNDRY, name: 'Smelter', key: 'building_foundry' },
            { type: FARM, name: 'Farm Estate', key: 'building_farm' },
            { type: MILL, name: 'Grain Mill', key: 'building_mill' },
            { type: MANOR, name: 'Manor', key: 'building_manor' },
            { type: BAZAAR, name: 'Bazaar', key: 'building_bazaar' },
            { type: WHARF, name: 'Wharf', key: 'building_wharf' },
            { type: CASA, name: 'Casa', key: 'building_casa' },
            { type: CELLARIUM, name: 'Cellarium', key: 'building_cellarium' },
            { type: CACHE, name: 'Cache', key: 'building_cache' },
            { type: BARRACKS, name: 'Barracks', key: 'building_barracks' },
            { type: CASTLE, name: 'Castle', key: 'building_castle' },
            { type: CITY_GUARD_HOUSE, name: 'Guardhouse', key: 'building_guard_house' },
            { type: SHIPYARD, name: 'Shipyard', key: 'building_shipyard' },
            { type: MOONGLOW, name: 'Sorcerer\'s Tower', key: 'building_moonglow' },
            { type: STABLES, name: 'Stable', key: 'building_stable' },
            { type: TEMPLE, name: 'Academy', key: 'building_temple' },
            { type: TRAINING_GROUND, name: 'Training Arena', key: 'building_training_ground' },
            { type: WORKSHOP, name: 'Blacksmith', key: 'building_workshop' }
        ];
        
        this.render();
    }
    
    /**
     * Count buildings of a specific type
     */
    countBuildings(buildingType) {
        if (!this.gameEngine) return 0;
        
        let count = 0;
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                if (this.gameEngine.map[i][j] === buildingType) {
                    count++;
                }
            }
        }
        return count;
    }
    
    /**
     * Render the building list
     */
    render() {
        if (!this.gameEngine) return;
        
        // Get counts for all buildings and filter to only show buildings with count > 0
        const buildingsWithCounts = this.buildings.map(building => {
            const count = this.countBuildings(building.type);
            return { ...building, count };
        }).filter(building => building.count > 0);
        
        // If no buildings, show empty message
        if (buildingsWithCounts.length === 0) {
            this.container.innerHTML = `
                <div class="building-list">
                    <h3>Buildings</h3>
                    <div class="building-empty">No buildings placed yet</div>
                </div>
            `;
            return;
        }
        
        // Build flex items for buildings with counts
        const buildingItems = buildingsWithCounts.map(building => {
            const imageKey = getTileImageKey(building.type) || building.key;
            const filename = this.getImageFilename(imageKey);
            
            return `
                <div class="building-item" title="${building.name}">
                    <img src="./assets/images/buildings/${filename}.png" 
                         alt="${building.name}" 
                         title="${building.name}"
                         class="building-icon"
                         onerror="this.style.display='none'" />
                    <span class="building-count">${this.formatNumber(building.count)}</span>
                </div>
            `;
        }).join('');
        
        this.container.innerHTML = `
            <div class="building-list">
                <h3>Buildings</h3>
                <div class="building-grid">
                    ${buildingItems}
                </div>
            </div>
        `;
    }
    
    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    
    /**
     * Format number with commas
     */
    formatNumber(num) {
        return Math.round(num).toLocaleString();
    }
    
    /**
     * Get image filename from asset key
     */
    getImageFilename(key) {
        // Map asset keys to actual filenames (matching AssetLoader paths)
        const filenameMap = {
            'building_townhall': 'basilica-fix-64',
            'building_forester': 'forester-v6-64',
            'building_sawmill': 'sawmill-64',
            'building_quarry': 'quarry-2-64',
            'building_stonemason': 'stonemasonery-64',
            'building_mine': 'iron-mine-64',
            'building_foundry': 'smelter-64',
            'building_farm': 'farm-v3-64',
            'building_mill': 'grain-mill-64-fix',
            'building_manor': 'villa-64',
            'building_bazaar': 'market-64',
            'building_wharf': 'ports-front-flip-64',
            'building_casa': 'basic_house-64',
            'building_cellarium': 'warehouse-v2-64',
            'building_cache': 'hideaway-64',
            'building_barracks': 'barracks-v2-64',
            'building_castle': 'castle-64-fix',
            'building_guard_house': 'guard\'s_house-v2-64',
            'building_shipyard': 'shipyard-front-flip-64',
            'building_moonglow': 'sorcerer\'s-tower-v2-64',
            'building_stable': 'stable-v2-fix-64',
            'building_temple': 'academy',
            'building_training_ground': 'training_arena-64',
            'building_workshop': 'blacksmith-v2-64'
        };
        
        return filenameMap[key] || key.replace('building_', '').replace(/_/g, '-') + '-64';
    }
    
    /**
     * Update the building list
     */
    update() {
        this.render();
    }
}

